// RatingStars.js

// Anzahl der maximalen Sterne
export const MAX_STARS = 5

// === 1. Dein SVG-Shape als Template-String ===
const STAR_SVG = `
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 22 20.97"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  stroke="currentColor"
  class="star-icon"
>
  <polygon points="11 .98 14.09 7.24 21 8.25 16 13.12 17.18 20 11 16.75 4.82 20 6 13.12 1 8.25 7.91 7.24 11 .98"/>
</svg>
`

/**
 * Erstellt eine **statische** Sterndarstellung mit Inline-SVGs.
 *
 * @param {number} rating - Aktuelle Bewertung (0 … maxStars)
 * @param {number} maxStars - Maximale Anzahl an Sternen
 * @param {Object} [options] - Zusätzliche Optionen
 *   • onlyEmptyStars (boolean): Wenn true, werden nur leere Sterne angezeigt
 *   • showRatingText (boolean): Wenn true, wird der Rating-Text in den Container eingebettet
 * @returns {HTMLElement} starsContainer
 */
export function createStaticStars(rating, maxStars = MAX_STARS, options = {}) {
    const starsContainer = document.createElement('div')
    starsContainer.className = options.onlyEmptyStars ? 'stars-container no-votes' : 'stars-container'

    // LEERE Sterne (Hintergrund)
    const emptyWrapper = document.createElement('div')
    emptyWrapper.className = 'empty-stars'
    for (let i = 0; i < maxStars; i++) {
        const starWrapper = document.createElement('div')
        starWrapper.className = 'star-wrapper'
        starWrapper.innerHTML = STAR_SVG
        emptyWrapper.appendChild(starWrapper)
    }
    starsContainer.appendChild(emptyWrapper)

    // GEFÜLLTE Sterne (Vordergrund)
    if (!options.onlyEmptyStars) {
        const filledWrapper = document.createElement('div')
        filledWrapper.className = 'filled-stars'
        for (let i = 0; i < maxStars; i++) {
            const starWrapper = document.createElement('div')
            starWrapper.className = 'star-wrapper'
            starWrapper.innerHTML = STAR_SVG
            // Direkt mit "active" markieren, damit CSS-Farbe = gold ist
            starWrapper.querySelector('svg').classList.add('active')
            filledWrapper.appendChild(starWrapper)
        }
        const widthPercent = ((rating || 0) / maxStars) * 100
        filledWrapper.style.width = `${widthPercent}%`
        emptyWrapper.appendChild(filledWrapper)
    }

    // Rating-Text hinzufügen wenn gewünscht
    if (options.showRatingText && !options.onlyEmptyStars) {
        const ratingText = document.createElement('span')
        ratingText.className = 'rating-text'
        ratingText.textContent = (rating || 0).toFixed(1)
        starsContainer.appendChild(ratingText)
    }

    return starsContainer
}

/**
 * Setzt die Breite der gefüllten Sterne neu (statisch).
 *
 * @param {HTMLElement} container - Ergebnis von createStaticStars()
 * @param {number} rating - Neue Bewertung (0 … maxStars)
 * @param {number} maxStars - Maximale Anzahl an Sternen
 */
export function updateFilledStarsWidth(container, rating, maxStars = MAX_STARS) {
    const filledWrapper = container.querySelector('.filled-stars')
    if (!filledWrapper) return
    const widthPercent = ((rating || 0) / maxStars) * 100
    filledWrapper.style.width = `${widthPercent}%`
}

/**
 * Erstellt eine **interaktive** Sterndarstellung für ein Modal (Inline-SVGs).
 *
 * @param {number} initialRating - Initiale Bewertung (0 … maxStars)
 * @param {number} maxStars - Maximale Anzahl an Sternen
 * @param {Function} onStarClick - Callback: function(ratingValue: number, submitImmediate: boolean)
 * @param {Function} [onStarHover] - Optional: Callback: function(ratingValue: number)
 * @returns {HTMLElement} starsContainer
 */
export function createModalStars(initialRating, maxStars = MAX_STARS, onStarClick, onStarHover) {
    const starsContainer = document.createElement('div')
    starsContainer.className = 'rw-direct-stars-container'

    let currentRating = initialRating

    for (let i = 1; i <= maxStars; i++) {
        const star = document.createElement('span')
        star.className = 'rw-direct-star'
        star.dataset.value = i
        star.innerHTML = STAR_SVG

        // Wenn ≤ initialRating, dann aktiv/farbig
        if (i <= initialRating) {
            star.querySelector('svg').classList.add('active')
        }

        if (typeof onStarClick === 'function') {
            star.addEventListener('click', () => {
                currentRating = i
                updateStarsDisplay(starsContainer, i) // hier wird unsere neue Funktion aufgerufen
                onStarClick(i, false)
            })
        }

        if (typeof onStarHover === 'function') {
            star.addEventListener('mouseover', () => {
                updateStarsDisplay(starsContainer, i)
                onStarHover(i)
            })
            star.addEventListener('mouseout', () => {
                updateStarsDisplay(starsContainer, currentRating)
            })
        }

        starsContainer.appendChild(star)
    }

    return starsContainer
}

/**
 * **Neu**: updateStarsDisplay – dieselbe Logik, die vorher "updateModalStarsDisplay" war.
 *
 * Aktualisiert die Klassen der SVGs im Modal-Container,
 * so dass die ersten `rating` Sterne die Klasse "active" bekommen.
 *
 * @param {HTMLElement} container - Ergebnis von createModalStars()
 * @param {number} rating - Neue Bewertung (1 … maxStars)
 */
export function updateStarsDisplay(container, rating) {
    const stars = container.querySelectorAll('.rw-direct-star')
    stars.forEach((star) => {
        const value = parseInt(star.dataset.value, 10)
        const svgEl = star.querySelector('svg')
        if (!svgEl) return
        if (value <= rating) {
            svgEl.classList.add('active')
        } else {
            svgEl.classList.remove('active')
        }
    })
}
